import { VoiceCallIdleMessage } from 'flow-schema';

import { api } from 'common';

interface GetDefaultEndCallToolDefinitionOptions {
  organizationId: string;
}
export function getDefaultEndCallToolDefinition(
  options: GetDefaultEndCallToolDefinitionOptions
): api.AssistantToolDefinition.EndCallToolDefinitionUpsertValues {
  const { organizationId } = options;
  return {
    type: api.AssistantToolDefinition.AssistantToolType.EndCall,
    name: api.AssistantToolDefinition.AssistantToolType.EndCall,
    organizationId,
    summary: null,
    startMessage: '',
  };
}

export const IDLE_MESSAGES_OPTIONS: {
  label: string;
  value: api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage;
}[] = [
  {
    label: VoiceCallIdleMessage.AreYouStillThere,
    value: api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage.AreYouStillThere,
  },
  {
    label: VoiceCallIdleMessage.IsThereAnythingElseYouNeedHelpWith,
    value:
      api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage
        .IsThereAnythingElseYouNeedHelpWith,
  },
  {
    label: VoiceCallIdleMessage.FeelFreeToAskMeAnyQuestions,
    value: api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage.FeelFreeToAskMeAnyQuestions,
  },
  {
    label: VoiceCallIdleMessage.HowCanIAssistYouFurther,
    value: api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage.HowCanIAssistYouFurther,
  },
  {
    label: VoiceCallIdleMessage.LetMeKnowIfThereIsAnythingYouNeed,
    value:
      api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage.LetMeKnowIfThereIsAnythingYouNeed,
  },
  {
    label: VoiceCallIdleMessage.ImStillHereIfYouNeedAssistance,
    value:
      api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage.ImStillHereIfYouNeedAssistance,
  },
  {
    label: VoiceCallIdleMessage.ImReadyToHelpWheneverYouAre,
    value: api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage.ImReadyToHelpWheneverYouAre,
  },
  {
    label: VoiceCallIdleMessage.IsThereSomethingSpecificYouAreLookingFor,
    value:
      api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage
        .IsThereSomethingSpecificYouAreLookingFor,
  },
  {
    label: VoiceCallIdleMessage.ImHereToHelpWithAnyQuestionsYouHave,
    value:
      api.Assistant.VoiceAssistantDefinition.VoiceCallIdleMessage
        .ImHereToHelpWithAnyQuestionsYouHave,
  },
];

export const VOICE_PERSONA_LABEL = 'Voice Persona';
export const VOICE_PERSONA_HELPER_TEXT = 'The persona and voice of the assistant.';

export const MULTILINGUAL_LABEL = 'Multilingual';
export const MULTILINGUAL_HELPER_TEXT =
  'Select this option to enable multilingual support. This will allow the assistant to respond in multiple languages.';

export const LANGUAGE_LABEL = 'Language';
export const LANGUAGE_HELPER_TEXT = 'The voice call will be conducted in this language.';

export const VOICE_SIMILARITY_BOOST_LABEL = 'Voice Similarity Boost';
export const ELEVEN_LABS_VOICE_SIMILARITY_BOOST_HELPER_TEXT = `Defines the similarity boost for voice settings. (0 - 1.0)`; // https://docs.vapi.ai/voice-fallback-plan#elevenlabs

export const VOICE_SPEED_LABEL = 'Voice Speed';
export const ELEVEN_LABS_VOICE_SPEED_HELPER_TEXT = `Defines the speed for voice settings. (0.7 - 1.2)`; // https://docs.vapi.ai/voice-fallback-plan#elevenlabs
export const CARTESIA_VOICE_SPEED_HELPER_TEXT = `Defines the speed for voice settings. (0.6 - 1.5)`; // https://docs.vapi.ai/voice-fallback-plan#cartesia

export const VOICE_STABILITY_LABEL = 'Voice Stability';
export const ELEVEN_LABS_VOICE_STABILITY_HELPER_TEXT = `Defines the stability of the voice. (0 - 1.0)`; // https://docs.vapi.ai/voice-fallback-plan#elevenlabs

export const EMOTION_RECOGNITION_LABEL = 'Emotion Recognition';
export const EMOTION_RECOGNITION_HELPER_TEXT = `Enable this property to detect user's emotions such as anger, joy, etc., and feed them as additional context to the model.`;

export const BACKGROUND_DENOISING_LABEL = 'Filter background noise while user is talking';
export const BACKGROUND_DENOISING_HELPER_TEXT = `Enable to filter background noise while the user is talking.`;

export const BACKGROUND_SOUND_LABEL = 'Background sound';
export const BACKGROUND_SOUND_HELPER_TEXT = `The background sound in the call.`;

export const START_SPEAKING_SMART_ENDPOINTING_LABEL = 'More accurate speech endpoint detection';
export const START_SPEAKING_SMART_ENDPOINTING_HELPER_TEXT = `Enable for more accurate speech endpoint detection.`;

export const SSML_PARSING_LABEL = 'SSML Parsing';
export const SSML_PARSING_HELPER_TEXT = `Enable to allow SSML tags to be used to control pausing, speaking speed, and other voice characteristics.`;

export const START_SPEAKING_WAIT_SECONDS_LABEL =
  'How many seconds to wait before starting to speak';
export const START_SPEAKING_WAIT_SECONDS_HELPER_TEXT = `Number of seconds the assistant waits before speaking. (0 - 5.0)`;

export const END_CALL_FUNCTION_LABEL = 'Allow assistant to end call from its side';
export const END_CALL_FUNCTION_HELPER_TEXT = `Enable this to allow the assistant to end the call from its side.`;

export const END_CALL_MESSAGE_LABEL = 'Message for end of call';
export const END_CALL_MESSAGE_HELPER_TEXT = `This is the message that the assistant will say if the call is ended. Note that the message will not be spoken if the call is ended by the user. Defaults to "Have a great day!"`;

export const IDLE_MESSAGES_LABEL = 'Idle Messages';
export const IDLE_MESSAGES_HELPER_TEXT = `The messages the assistant will speak when the user hasn't responded for Idle Timeout Seconds.`;

export const IDLE_MESSAGE_MAX_SPOKEN_COUNT_LABEL = 'Max idle messages';
export const IDLE_MESSAGE_MAX_SPOKEN_COUNT_HELPER_TEXT = `The maximum number of times Idle Messages can be spoken during the call. (1 - 10)`;

export const IDLE_TIMEOUT_SECONDS_LABEL = 'Idle timeout seconds';
export const IDLE_TIMEOUT_SECONDS_HELPER_TEXT = `The timeout in seconds before a message from Idle Messages is spoken. The clock starts when the assistant finishes speaking and remains active until the user speaks. (5 - 60)`;

export const STOP_SPEAKING_NUMBER_OF_WORDS_LABEL =
  'How many words the callee has to say for the assistant to stop talking';
export const STOP_SPEAKING_NUMBER_OF_WORDS_HELPER_TEXT = `The number of words the callee has to say before the assistant will stop talking. (0 - 10)`;

export const STOP_SPEAKING_VOICE_SECONDS_LABEL =
  'How many seconds the callee has to speak for the assistant to stop talking';
export const STOP_SPEAKING_VOICE_SECONDS_HELPER_TEXT = `The number of seconds the callee has to speak before the assistant will stop talking. (0 - 0.5)`;

export const STOP_SPEAKING_VOICE_BACK_OFF_SECONDS_LABEL =
  'How many seconds to wait before starting to speak after being interrupted';
export const STOP_SPEAKING_VOICE_BACK_OFF_SECONDS_HELPER_TEXT = `The number of seconds the assistant will wait before speaking again after being interrupted. (0 - 10)`;

export const MAX_CALL_DURATION_HELPER_TEXT = `Maximum duration for the actual call conversation. After this duration, the call will be ended gracefully.`;
